import { createContext, ReactNode, useEffect } from 'react';
import { applyMaskToData, MaskMode } from '@intigral/utils';
import type { NetworkErrorInfo } from '@intigral/utils';
import { KEYS } from './MixpanelConstants';
import { getDayOfWeek, getHourOfDay } from './MixpanelHelper';
import user from '../helpers/user';
import type { UserInfo } from '@intigral/domain';
import h from '../helpers/common';
import { getBaseSubscription } from '@intigral/utils/lib/user';
import { isGuestUser } from '../config/guestMode';

export interface MixpanelContextType {
    track: (name: string, value: Record<string, any>) => void;
    trackError: (eventName: string, errorInfo: NetworkErrorInfo) => void;
    incrementProperty: (property: string, value?: number) => void;
    setUserProperties: (properties: Record<string, any>, hasList?: boolean) => void;
    setSuperProperties: (properties: Record<string, any>, hasOnce?: boolean) => void;
    setAlias: (value: string) => void;
    setIdentity: (userId: string) => void;
    setTimeEvent: (eventName?: string) => void;
    reset: () => void;
}

// Create context with initial default value
export const MixpanelContext = createContext<MixpanelContextType | undefined>(undefined);

interface MixpanelContextProviderProps {
    analytic: any;
    user: any;
    children: ReactNode;
}

const MixpanelContextProvider: React.FC<MixpanelContextProviderProps> = ({ analytic, children }) => {
    const userData = user.getUserInfo() as UserInfo;
    const isActive = userData?.tierStatus ? userData?.tierStatus?.active : getBaseSubscription(userData)?.active;
    const TierName = userData?.tierConfig?.tierName;
    const Plan = { Free: 'Basic', Plus: 'Premium', Paid: 'Premium' }[TierName] || TierName;
    const Type = isActive ? { Basic: 'Free', Premium: 'Paid' }[Plan] || 'Free' : 'Inactive';
    const maskEnabled = h.getAppConfig()?.analytics?.mixpanel?.enable_masking; // CMS flag for masking
    const isGuest = isGuestUser(userData);
    const peopleProperties = {
        $email: userData?.email,
        $name: userData?.username,
        $braze_external_id: userData?.uid || userData?.accountId,
        'User Segment': userData?.segment,
        distinct_id: userData?.uid || userData?.accountId,
        // $user_id: userData?.accountId,
        //'Email Verified': emailVerified,
        'Subscription Tier': TierName,
        'Subscription Type': Type,
        'Subscription Plan': Plan,
        'App Language': h.getLang() === 'ar' ? 'AR' : 'EN',
        'First App Login': userData?.firstLoginDate ? new Date(userData?.firstLoginDate).toISOString() : 'null',
        'Login time': new Date().toISOString(),
        [KEYS.PEOPLE.GUEST_USER]: isGuest,
        //'4K Support': uhdSupport(),
    };

    // Uncomment the following line for PDPL:
    if (maskEnabled) applyMaskToData(peopleProperties, MaskMode.Partial, true);

    useEffect(() => {
        if (!analytic) return;
        analytic.register({ [KEYS.SUPER.GUEST_USER]: isGuest });
        analytic.people?.set?.({ [KEYS.PEOPLE.GUEST_USER]: isGuest });
    }, [analytic, isGuest]);

    const setIdentity = (userId: string) => {
        analytic.identify(userId);
        analytic.people.unset(KEYS.PEOPLE.PHONE);
    };

    const setAlias = (value: string) => {
        analytic.alias(value);
    };

    const reset = () => {
        analytic.reset();
    };

    const setSuperProperties = (properties: Record<string, any>, hasOnce = false) => {
        if (typeof properties === 'object') {
            if (hasOnce) {
                analytic.register_once(properties);
            } else {
                analytic.register(properties);
            }
        }
    };

    const setUserProperties = (properties: Record<string, any>, hasList = false) => {
        if (typeof properties === 'object') {
            if (hasList) {
                analytic.people.append(properties);
            } else {
                analytic.people.set(properties);
            }
        }
    };

    const incrementProperty = (property: string, value?: number) => {
        if (typeof value === 'undefined') {
            analytic.people.increment(property);
        } else {
            analytic.people.increment(property, value);
        }
    };

    const track = (name: string, value?: Record<string, any>) => {
        const guestUser = isGuestUser(user.getUserInfo() as UserInfo);
        setSuperProperties({ [KEYS.SUPER.HOUR_OF_DAY]: getHourOfDay() });
        setSuperProperties({ [KEYS.SUPER.DAY_OF_WEEK]: getDayOfWeek() });
        setSuperProperties({ [KEYS.SUPER.GUEST_USER]: guestUser });
        analytic.track(name, {
            ...peopleProperties,
            ...value,
            [KEYS.PEOPLE.GUEST_USER]: guestUser,
        });
    };

    const setTimeEvent = (eventName = '') => {
        analytic.time_event(eventName);
    };

    const trackError = (_eventName: string, _errorInfo: NetworkErrorInfo) => {
        // API failure / error Mixpanel events are no longer sent from WCP.
        return;
    };

    return (
        <MixpanelContext.Provider
            value={{
                track,
                trackError,
                incrementProperty,
                setUserProperties,
                setSuperProperties,
                setAlias,
                setIdentity,
                setTimeEvent,
                reset,
            }}
        >
            {children}
        </MixpanelContext.Provider>
    );
};

export default MixpanelContextProvider;
